<script lang="ts" generics="TCommitReceipt = undefined">
  import type { Snippet } from "svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import StepActionCTA from "../../../step-action-cta"
  import type {
    ContactOrderPlanningContinuationResult,
    ContactOrderPlanningOperations,
    ContactOrderPlanningSubmissionPhase,
    ContactOrderPlanningSubmissionResult,
  } from "../../../../_logic/contact-orders-contract"
  import CreateContactOrdersAction from "./_modules/create-contact-orders-action"
  import type { ContactOrderCreateIntent } from "../../create-contact-orders-contract"

  type Props = {
    children?: Snippet<[Snippet, Snippet]>
    loading: boolean
    phase: ContactOrderPlanningSubmissionPhase
    createIntent: ContactOrderCreateIntent
    continuationResult: ContactOrderPlanningContinuationResult
    operations: ContactOrderPlanningOperations<TCommitReceipt>
    showContinueButton: boolean
    backButtonLabel: string
    continueButtonLabel: string
    onBack?: (() => void) | null
    onPhaseChange: (phase: ContactOrderPlanningSubmissionPhase) => void
    onSubmissionResult: (
      result: ContactOrderPlanningSubmissionResult<TCommitReceipt>,
    ) => void | Promise<void>
  }

  const {
    children: renderActions = null,
    loading,
    phase,
    createIntent,
    continuationResult,
    operations,
    showContinueButton,
    backButtonLabel,
    continueButtonLabel,
    onBack = null,
    onPhaseChange,
    onSubmissionResult,
  }: Props = $props()

  let continuing = $state(false)
  const pending = $derived(phase !== "idle")
  const continueDisabledReason = $derived(
    phase === "creating_contact_orders"
      ? "Ankündigungspläne werden gerade angelegt."
      : continuing || phase === "continuing"
        ? "Der Workflow wird gerade fortgesetzt."
        : loading
          ? "Termine werden gerade geladen."
          : null,
  )

  const continueWorkflow = async () => {
    if (continuing || phase !== "idle" || loading) return

    continuing = true
    onPhaseChange("continuing")
    try {
      await onSubmissionResult({ kind: "continued", result: continuationResult })
    } catch (error) {
      console.error("contact_order_planning_surface_continue_failed", error)
      newNotification({
        class: "error",
        html: error instanceof Error ? error.message : "Workflow konnte nicht fortgesetzt werden.",
      })
    } finally {
      continuing = false
      onPhaseChange("idle")
    }
  }
</script>

<CreateContactOrdersAction
  intent={createIntent}
  {operations}
  {phase}
  {onPhaseChange}
  {onSubmissionResult}
>
  {#snippet children(createAction)}
    {#snippet primaryAction()}
      {#if !createIntent.hidden}
        {@render createAction()}
      {/if}
    {/snippet}

    {#snippet footerActions()}
      <div class="step-actions">
        {#if onBack}
          <CTA
            type="secondaryButton"
            content={backButtonLabel}
            disabled={pending}
            onClick={onBack}
          />
        {/if}
        {#if showContinueButton}
          <StepActionCTA
            type="secondaryButton"
            content={continuing ? "Bitte warten…" : continueButtonLabel}
            onClick={continueWorkflow}
            disabled={Boolean(continueDisabledReason)}
            disabledReason={continueDisabledReason}
          />
        {/if}
        {@render primaryAction()}
      </div>
    {/snippet}

    {#if renderActions}
      {@render renderActions(primaryAction, footerActions)}
    {:else}
      {@render footerActions()}
    {/if}
  {/snippet}
</CreateContactOrdersAction>

<style lang="less">
  .step-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    flex-wrap: wrap;
    margin-top: 0.4rem;
  }
</style>
